import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  MessageSquare,
  Plus,
  Mail,
  MailOpen,
  AlertCircle,
  Clock,
  Trash2,
  Printer,
  User,
  Reply,
  Wifi,
  WifiOff,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Convert http(s) to ws(s) for WebSocket URL
const WS_URL = BACKEND_URL.replace(/^http/, 'ws');

const priorityConfig = {
  low: { color: "bg-slate-100 text-slate-700", label: "Low" },
  normal: { color: "bg-blue-100 text-blue-700", label: "Normal" },
  high: { color: "bg-amber-100 text-amber-700", label: "High" },
  urgent: { color: "bg-red-100 text-red-700", label: "Urgent" },
};

export default function CommunicationPage() {
  const { user, token } = useAuth();
  const [communications, setCommunications] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterRead, setFilterRead] = useState("all");
  const [selectedComm, setSelectedComm] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  
  // WebSocket state
  const [wsConnected, setWsConnected] = useState(false);
  const [wsReconnecting, setWsReconnecting] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  
  // Reply state
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  
  // Typing indicator state
  const [typingUsers, setTypingUsers] = useState({});
  
  // Recipient selection state
  const [sendToType, setSendToType] = useState("all"); // "all" or "specific"

  const [newMessage, setNewMessage] = useState({
    subject: "",
    message: "",
    priority: "normal",
    recipients: [],
  });

  // WebSocket message handler
  const handleWsMessage = useCallback((event) => {
    try {
      const data = JSON.parse(event.data);
      
      switch (data.type) {
        case "connection_established":
          console.log("WebSocket connected:", data.message);
          break;
          
        case "new_message":
          // Add new message to the list
          setCommunications(prev => {
            // Check if message already exists
            if (prev.some(c => c.id === data.communication.id)) {
              return prev;
            }
            return [{ ...data.communication, is_read: false }, ...prev];
          });
          // Show notification toast for new messages
          if (data.communication.author_id !== user?.id) {
            toast.info(`New message from ${data.author_name}: ${data.communication.subject}`, {
              duration: 5000,
            });
          }
          break;
          
        case "new_reply":
          // Update the communication with the new reply
          setCommunications(prev => prev.map(comm => {
            if (comm.id === data.comm_id) {
              return {
                ...comm,
                replies: [...(comm.replies || []), data.reply]
              };
            }
            return comm;
          }));
          // Update selected comm if viewing it
          setSelectedComm(prev => {
            if (prev && prev.id === data.comm_id) {
              return {
                ...prev,
                replies: [...(prev.replies || []), data.reply]
              };
            }
            return prev;
          });
          // Show notification for new replies
          if (data.reply.author_id !== user?.id) {
            toast.info(`New reply from ${data.reply.author_name}`, {
              duration: 3000,
            });
          }
          break;
          
        case "message_deleted":
          // Remove deleted message from list
          setCommunications(prev => prev.filter(c => c.id !== data.comm_id));
          // Close view dialog if viewing deleted message
          if (selectedComm?.id === data.comm_id) {
            setViewDialogOpen(false);
            setSelectedComm(null);
          }
          break;
          
        case "message_read":
          // Could show read receipts here if needed
          break;
          
        case "user_typing":
          // Show typing indicator
          if (data.user_id !== user?.id) {
            setTypingUsers(prev => ({
              ...prev,
              [data.comm_id]: { name: data.user_name, timestamp: Date.now() }
            }));
            // Clear typing indicator after 3 seconds
            setTimeout(() => {
              setTypingUsers(prev => {
                const updated = { ...prev };
                if (updated[data.comm_id]?.timestamp < Date.now() - 2500) {
                  delete updated[data.comm_id];
                }
                return updated;
              });
            }, 3000);
          }
          break;
          
        case "pong":
          // Heartbeat response received
          break;
          
        default:
          console.log("Unknown WebSocket message type:", data.type);
      }
    } catch (error) {
      console.error("Error handling WebSocket message:", error);
    }
  }, [user?.id, selectedComm?.id]);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    if (!token || wsRef.current?.readyState === WebSocket.OPEN) return;
    
    setWsReconnecting(true);
    
    try {
      const ws = new WebSocket(`${WS_URL}/api/communications/ws?token=${token}`);
      
      ws.onopen = () => {
        console.log("WebSocket connected");
        setWsConnected(true);
        setWsReconnecting(false);
        
        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, 30000);
      };
      
      ws.onmessage = handleWsMessage;
      
      ws.onclose = (event) => {
        console.log("WebSocket closed:", event.code, event.reason);
        setWsConnected(false);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        
        // Attempt to reconnect after 5 seconds (unless it was an auth error)
        if (event.code !== 4001) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, 5000);
        }
      };
      
      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        setWsReconnecting(false);
      };
      
      wsRef.current = ws;
    } catch (error) {
      console.error("Failed to create WebSocket:", error);
      setWsReconnecting(false);
    }
  }, [token, handleWsMessage]);

  // Disconnect WebSocket
  const disconnectWebSocket = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setWsConnected(false);
  }, []);

  // Send typing indicator
  const sendTypingIndicator = useCallback((commId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "typing",
        comm_id: commId
      }));
    }
  }, []);

  useEffect(() => {
    fetchCommunications();
    fetchEmployees();
    connectWebSocket();
    
    return () => {
      disconnectWebSocket();
    };
  }, [connectWebSocket, disconnectWebSocket]);

  const fetchCommunications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/communications`);
      setCommunications(response.data);
    } catch (error) {
      console.error("Failed to fetch communications:", error);
      toast.error("Failed to load communications");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error("Failed to fetch employees:", error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.subject.trim() || !newMessage.message.trim()) {
      toast.error("Please enter subject and message");
      return;
    }
    
    // Validate recipient selection if specific person is chosen
    if (sendToType === "specific" && newMessage.recipients.length === 0) {
      toast.error("Please select a recipient");
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/communications`, newMessage);
      toast.success("Message sent successfully!");
      setDialogOpen(false);
      setNewMessage({
        subject: "",
        message: "",
        priority: "normal",
        recipients: [],
      });
      setSendToType("all");
      // WebSocket will handle updating the list automatically
      // Only fetch if not connected via WebSocket
      if (!wsConnected) {
        fetchCommunications();
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleMarkRead = async (commId) => {
    try {
      await axios.patch(`${API}/communications/${commId}/read`);
      // Update local state immediately
      setCommunications(prev => prev.map(c => 
        c.id === commId ? { ...c, is_read: true } : c
      ));
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  const handleDelete = async (commId) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;
    
    try {
      await axios.delete(`${API}/communications/${commId}`);
      toast.success("Message deleted");
      // WebSocket will handle removing from list
      if (!wsConnected) {
        setCommunications(prev => prev.filter(c => c.id !== commId));
      }
      setViewDialogOpen(false);
    } catch (error) {
      console.error("Failed to delete message:", error);
      toast.error(error.response?.data?.detail || "Failed to delete message");
    }
  };

  const handleViewMessage = async (comm) => {
    setSelectedComm(comm);
    setViewDialogOpen(true);
    if (!comm.is_read) {
      handleMarkRead(comm.id);
    }
  };

  const handlePrint = (comm) => {
    const printContent = `
      <html>
        <head>
          <title>Communication - ${comm.subject}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; }
            .header { border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 20px; }
            .subject { font-size: 24px; font-weight: bold; }
            .meta { color: #666; margin: 10px 0; }
            .priority { display: inline-block; padding: 4px 12px; border-radius: 4px; font-size: 12px; }
            .priority-urgent { background: #fee2e2; color: #dc2626; }
            .priority-high { background: #fef3c7; color: #d97706; }
            .priority-normal { background: #dbeafe; color: #2563eb; }
            .priority-low { background: #f1f5f9; color: #475569; }
            .message { margin-top: 30px; line-height: 1.6; white-space: pre-wrap; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="subject">${comm.subject}</div>
            <div class="meta">
              From: ${comm.author_name} | 
              Date: ${new Date(comm.created_at).toLocaleString()} |
              <span class="priority priority-${comm.priority}">${comm.priority.toUpperCase()}</span>
            </div>
          </div>
          <div class="message">${comm.message}</div>
        </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) {
      toast.error("Please enter a reply");
      return;
    }

    setSendingReply(true);
    try {
      await axios.post(`${API}/communications/${selectedComm.id}/reply`, {
        message: replyText
      });
      toast.success("Reply sent!");
      setReplyText("");
      // WebSocket will handle updating the replies automatically
      // Only fetch manually if not connected
      if (!wsConnected) {
        const response = await axios.get(`${API}/communications/${selectedComm.id}`);
        setSelectedComm(response.data);
        fetchCommunications();
      }
    } catch (error) {
      console.error("Failed to send reply:", error);
      toast.error("Failed to send reply");
    } finally {
      setSendingReply(false);
    }
  };

  const filteredCommunications = communications.filter((comm) => {
    if (filterPriority !== "all" && comm.priority !== filterPriority) return false;
    if (filterRead === "unread" && comm.is_read) return false;
    if (filterRead === "read" && !comm.is_read) return false;
    return true;
  });

  const unreadCount = communications.filter((c) => !c.is_read).length;

  return (
    <Layout>
      {/* Page Header */}
      <div className="page-header">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="page-title">Communication</h1>
              {/* WebSocket Connection Status */}
              <div className="flex items-center gap-1.5" title={wsConnected ? "Real-time updates active" : "Reconnecting..."}>
                {wsConnected ? (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                    <Wifi className="w-3 h-3" />
                    Live
                  </span>
                ) : wsReconnecting ? (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    Connecting...
                  </span>
                ) : (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
                    <WifiOff className="w-3 h-3" />
                    Offline
                  </span>
                )}
              </div>
            </div>
            <p className="page-subtitle">
              Interoffice messaging and announcements
              {unreadCount > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                  {unreadCount} unread
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!wsConnected && (
              <Button
                variant="outline"
                size="sm"
                onClick={connectWebSocket}
                className="gap-2"
                disabled={wsReconnecting}
              >
                <RefreshCw className={`w-4 h-4 ${wsReconnecting ? 'animate-spin' : ''}`} />
                Reconnect
              </Button>
            )}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="new-message-btn">
                  <Plus className="w-4 h-4" />
                  New Message
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>New Message</DialogTitle>
                <DialogDescription>
                  Send a message to all staff or specific recipients
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSendMessage}>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject *</Label>
                    <Input
                      id="subject"
                      placeholder="Enter subject"
                      value={newMessage.subject}
                      onChange={(e) =>
                        setNewMessage((prev) => ({ ...prev, subject: e.target.value }))
                      }
                      data-testid="comm-subject"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      placeholder="Enter your message..."
                      value={newMessage.message}
                      onChange={(e) =>
                        setNewMessage((prev) => ({ ...prev, message: e.target.value }))
                      }
                      rows={5}
                      data-testid="comm-message"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Priority</Label>
                      <Select
                        value={newMessage.priority}
                        onValueChange={(val) =>
                          setNewMessage((prev) => ({ ...prev, priority: val }))
                        }
                      >
                        <SelectTrigger data-testid="comm-priority">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="urgent">Urgent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Send To</Label>
                      <Select
                        value={sendToType}
                        onValueChange={(val) => {
                          setSendToType(val);
                          if (val === "all") {
                            setNewMessage((prev) => ({ ...prev, recipients: [] }));
                          }
                        }}
                      >
                        <SelectTrigger data-testid="comm-recipients">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Staff</SelectItem>
                          <SelectItem value="specific">Specific Person</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Show employee selection when "Specific Person" is chosen */}
                  {sendToType === "specific" && (
                    <div className="space-y-2">
                      <Label>Select Recipient *</Label>
                      <Select
                        value={newMessage.recipients[0] || ""}
                        onValueChange={(val) => {
                          setNewMessage((prev) => ({ ...prev, recipients: [val] }));
                        }}
                      >
                        <SelectTrigger data-testid="comm-recipient-select">
                          <SelectValue placeholder="Choose a person..." />
                        </SelectTrigger>
                        <SelectContent>
                          {employees.map((emp) => (
                            <SelectItem key={emp.id} value={emp.id}>
                              {emp.name} ({emp.role})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  <p className="text-xs text-slate-500">
                    Select "All Staff" to broadcast to everyone, or choose a specific person.
                  </p>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={sending} data-testid="send-message-btn">
                    {sending ? "Sending..." : "Send Message"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-40" data-testid="filter-priority">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="normal">Normal</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterRead} onValueChange={setFilterRead}>
          <SelectTrigger className="w-40" data-testid="filter-read">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Messages</SelectItem>
            <SelectItem value="unread">Unread</SelectItem>
            <SelectItem value="read">Read</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Messages List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-slate-500">Loading messages...</p>
        </div>
      ) : filteredCommunications.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
          <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-700 mb-1">No Messages</h3>
          <p className="text-slate-500 text-sm">
            {communications.length === 0
              ? "Start the conversation by sending a new message"
              : "No messages match your filters"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredCommunications.map((comm) => (
            <div
              key={comm.id}
              className={`p-4 bg-white rounded-lg border cursor-pointer transition-colors hover:border-teal-300 ${
                !comm.is_read ? "border-l-4 border-l-teal-500 border-slate-200" : "border-slate-200"
              }`}
              onClick={() => handleViewMessage(comm)}
              data-testid={`comm-item-${comm.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    {comm.is_read ? (
                      <MailOpen className="w-5 h-5 text-slate-400" />
                    ) : (
                      <Mail className="w-5 h-5 text-teal-600" />
                    )}
                  </div>
                  <div>
                    <h4 className={`font-medium ${!comm.is_read ? "text-slate-900" : "text-slate-600"}`}>
                      {comm.subject}
                    </h4>
                    <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                      {comm.message}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {comm.author_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(comm.created_at).toLocaleDateString()}
                      </span>
                      {comm.recipients && comm.recipients.length > 0 && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                          To: {employees.find(e => e.id === comm.recipients[0])?.name || "Specific"}
                        </span>
                      )}
                      {comm.replies && comm.replies.length > 0 && (
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">
                          {comm.replies.length} {comm.replies.length === 1 ? "reply" : "replies"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      priorityConfig[comm.priority]?.color || priorityConfig.normal.color
                    }`}
                  >
                    {comm.priority === "urgent" && <AlertCircle className="w-3 h-3 inline mr-1" />}
                    {priorityConfig[comm.priority]?.label || "Normal"}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View Message Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={(open) => {
        setViewDialogOpen(open);
        if (!open) {
          setReplyText("");
        }
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedComm && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      priorityConfig[selectedComm.priority]?.color
                    }`}
                  >
                    {selectedComm.priority.toUpperCase()}
                  </span>
                  {selectedComm.subject}
                </DialogTitle>
                <DialogDescription>
                  From {selectedComm.author_name} • {new Date(selectedComm.created_at).toLocaleString()}
                </DialogDescription>
              </DialogHeader>
              <div className="py-4 space-y-4">
                <div className="bg-slate-50 p-4 rounded-lg whitespace-pre-wrap text-sm">
                  {selectedComm.message}
                </div>
                
                {/* Replies Section */}
                {selectedComm.replies && selectedComm.replies.length > 0 && (
                  <div className="border-t pt-4">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3">
                      Replies ({selectedComm.replies.length})
                    </h4>
                    <div className="space-y-3">
                      {selectedComm.replies.map((reply, index) => (
                        <div key={index} className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-300">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-slate-800">{reply.author_name}</span>
                            <span className="text-xs text-slate-500">
                              {new Date(reply.created_at).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">{reply.message}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Reply Input */}
                <div className="border-t pt-4">
                  <Label className="text-sm font-semibold text-slate-700 mb-2 block">Reply</Label>
                  <Textarea
                    placeholder="Write your reply..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={3}
                    className="mb-2"
                    data-testid="reply-textarea"
                  />
                  <Button 
                    size="sm" 
                    onClick={handleSendReply}
                    disabled={sendingReply || !replyText.trim()}
                    className="gap-2"
                    data-testid="send-reply-btn"
                  >
                    <Reply className="w-4 h-4" />
                    {sendingReply ? "Sending..." : "Send Reply"}
                  </Button>
                </div>
              </div>
              <DialogFooter className="flex justify-between">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePrint(selectedComm)}
                    className="gap-1"
                  >
                    <Printer className="w-4 h-4" />
                    Print
                  </Button>
                  {(selectedComm.author_id === user?.id || user?.role === "admin") && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 gap-1"
                      onClick={() => handleDelete(selectedComm.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </Button>
                  )}
                </div>
                <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
